/**
 * The Empty screen — `docs/property-maps-v2.md` §5.2, board 1.
 *
 * Two choices, side by side, because they are genuinely different jobs rather
 * than two routes to one thing: a **map** is a drawing crews read on a ticket,
 * and **measuring** is a number that ends up on a quote. The first card is
 * ringed as the recommended one; the second is plain.
 *
 * Mockup widths: 436px each, 20px apart, stacking below 1100px. The strip and
 * the footnote are 892px — the width of the pair — so the three blocks share
 * one left and right edge.
 *
 * Without edit permission neither button is drawn and one sentence replaces
 * them (§5.2's last line): a disabled button is a control that looks on and is
 * not, and a person who cannot make a map does not need to be told twice.
 */
import type { ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { measurementText } from '../measurement-text.js'
import { Button } from '../../components/primitives.js'
import { IconCheck, IconMaps, IconPlus, IconRuler } from '../../frame/icons.js'
import type { MeasurementGroup } from '../api.js'

export function MapEmptyScreen({
  propertyName, address, measurements, imagery, canEdit, offerMeasuring,
  onCreate, onMeasure, onSeeMeasurements,
}: {
  propertyName: string
  address: string
  measurements: MeasurementGroup[]
  /**
   * What the provider said.
   *
   * Three states, not two: still asking, ready, and *not available* — the
   * first version had `null` mean both "asking" and "failed", so a company
   * with no tiles key read **Checking the aerial imagery…** for ever.
   */
  imagery: { attribution: string; capturedAt?: string } | 'asking' | 'unavailable'
  canEdit: boolean
  /** The `measurements` feature. Off means one card, centred (§5.2). */
  offerMeasuring: boolean
  onCreate: () => void
  onMeasure: () => void
  onSeeMeasurements: () => void
}) {
  const measured = measurements.filter((group) => group.total > 0)

  return (
    <div className="flex flex-col items-center gap-5 px-4 py-9">
      <div className="flex flex-col items-center gap-1.5 text-center">
        <b className="text-[22px] font-bold tracking-[-0.01em]">
          No maps for {propertyName} yet
        </b>
        <span className="text-[14px] text-text-muted">
          Pick how you want to start. You can always do the other later.
        </span>
      </div>

      <div className="flex flex-wrap justify-center gap-5">
        <ChoiceCard
          ringed
          icon={<IconMaps size={24} />}
          title="Create a new map"
          body={'A map is a named drawing on the aerial photo. Make a snow plan, a mowing'
            + ' plan, an irrigation map — then add layers to it for the things crews need'
            + ' to see.'}
          lines={[
            'Layers for zones, routes, hazards and notes',
            'Share each layer with all crews, or only some divisions',
            'Shows on the crew app and prints with a legend',
          ]}
          action={canEdit
            ? (
              <Button
                variant="primary"
                size="big"
                className="mt-1 w-full text-[14.5px]"
                onClick={onCreate}
              >
                <IconPlus size={16} />
                Create a map
              </Button>
            )
            : null}
        />

        {offerMeasuring && (
          <ChoiceCard
            icon={<IconRuler size={24} />}
            title="Just measure the property"
            body={'Outline turf, beds, hardscape or edging on the aerial to get square feet'
              + ' and linear feet. Save the numbers to the property when you’re happy with'
              + ' them.'}
            lines={[
              'No map to name, no layers to set up',
              'Numbers go to the property’s measurements',
              'Changed your mind? Keep the drawing as a map later',
            ]}
            action={canEdit
              ? (
                <Button size="big" className="mt-1 w-full text-[14.5px]" onClick={onMeasure}>
                  <IconRuler size={16} />
                  Start measuring
                </Button>
              )
              : null}
          />
        )}
      </div>

      {!canEdit && (
        <p className="m-0 text-body text-text-muted">
          No one has made a map for this property yet.
        </p>
      )}

      {/*
        * What the provider is doing, in one line with a dot.
        *
        * The capture clause appears **only when the provider returns one**
        * (§9.1): Google's 2D Tiles API does not, so v1 shows the attribution
        * and the address alone rather than a date somebody could plan around.
        */}
      <span className="inline-flex items-center gap-2 text-cell text-text-muted">
        <i
          className={cn(
            'h-2 w-2 rounded-chip',
            imagery === 'asking' ? 'bg-line-strong'
              : imagery === 'unavailable' ? 'bg-warn'
                : 'bg-accent',
          )}
          aria-hidden
        />
        {imagery === 'asking' && 'Checking the aerial imagery…'}
        {imagery === 'unavailable'
          && `Aerial imagery isn’t set up yet — you can still draw on a plain base · ${address}`}
        {typeof imagery === 'object' && [
          'Imagery ready',
          imagery.attribution,
          ...(imagery.capturedAt ? [`captured ${imagery.capturedAt}`] : []),
          address,
        ].join(' · ')}
      </span>

      {measured.length > 0 && (
        <div className={cn(
          'flex w-full max-w-[892px] items-center gap-4 rounded-record border border-line',
          'bg-surface px-[18px] py-3.5',
        )}
        >
          <span className={cn(
            'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-tile-large',
            'bg-surface-group text-text-body',
          )}
          >
            <IconRuler size={18} />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
            <b className="text-card">Already measured on this property</b>
            <span className="text-cell text-text-muted">
              {measured.map(measurementText).join(' · ')}
              {' — from earlier walks and aerial measuring.'}
            </span>
          </div>
          <button
            type="button"
            onClick={onSeeMeasurements}
            className="shrink-0 text-body font-semibold text-accent hover:text-accent-dark"
          >
            See in Overview ›
          </button>
        </div>
      )}

      <p className={cn(
        'm-0 w-full max-w-[892px] text-center text-[12px] leading-[1.5] text-text-faint',
      )}
      >
        Maps are internal. Crews see the layers you share with them on tickets for this
        property; nothing here ever appears in the customer portal.
      </p>
    </div>
  )
}

function ChoiceCard({
  icon, title, body, lines, action, ringed,
}: {
  icon: ReactNode
  title: string
  body: string
  lines: string[]
  action: ReactNode
  ringed?: boolean
}) {
  return (
    <div className={cn(
      'flex w-[436px] max-w-full shrink-0 flex-col gap-3.5 rounded-dialog border bg-surface',
      'px-6 py-[22px]',
      ringed ? 'border-[1.5px] border-accent-line ring-[3px] ring-accent-ring' : 'border-line',
    )}
    >
      <span className={cn(
        'inline-flex h-12 w-12 items-center justify-center rounded-[14px]',
        ringed ? 'bg-accent-soft text-accent' : 'bg-surface-group text-text-body',
      )}
      >
        {icon}
      </span>

      <div className="flex flex-col gap-1.5">
        <b className="text-[18px] font-bold tracking-[-0.01em]">{title}</b>
        <span className="text-[13.5px] leading-[1.5] text-text-body">{body}</span>
      </div>

      <div className="flex flex-col gap-1.5">
        {lines.map((line) => (
          <div key={line} className="flex items-center gap-2 text-cell text-text-body">
            <span className="inline-flex text-accent" aria-hidden><IconCheck size={14} /></span>
            {line}
          </div>
        ))}
      </div>

      {action}
    </div>
  )
}
